import { Injectable, Type, Optional } from '@angular/core';
import { Routes, Route } from '@angular/router';
import { NavigationRoute } from '../../models/config';
import { NavItem } from '../../models/navItem';
import { LoggingService } from '../shared/logging/logging.service';

// Import all components
import { TournamentsComponent } from '../../pages/tournaments/tournaments.component';
import { TournamentDetailComponent } from '../../components/tournament/tournament-detail/tournament-detail.component';
import { RoundsComponent } from '../../components/rounds/rounds.component';
import { ScorecardScoresComponent } from '../../pages/scorecard-scores/scorecard-scores.component';
import { ScorecardStatsComponent } from '../../pages/scorecard-stats/scorecard-stats.component';
import { ScorecardVerifyComponent } from '../../pages/scorecard-verify/scorecard-verify.component';
import { PlayerDetailComponent } from '../../components/players/player-detail/player-detail.component';
import { TournamentCourseDetailComponent } from '../../components/tournament/tournament-course-detail/tournament-course-detail.component';
import { CourseDetailComponent } from '../../components/courses/course-detail/course-detail.component';
import { CourseListComponent } from '../../components/courses/course-list/course-list.component';
import { TournamentPlayersComponent } from '../../components/tournament/tournament-players/tournament-players.component';
import { PurseComponent } from '../../components/tournament/purse/purse.component';
import { ComingSoonComponent } from '../../components/shared/coming-soon/coming-soon.component';
import { PlayerListComponent } from '../../components/players/player-list/player-list.component';
import { SystemComponent } from '../../pages/system/system.component';
import { RealTimeEventsComponent } from '../../components/real-time-events/real-time-events.component';
import { ConfigComponent } from '../../components/config/config.component';
import { TournamentCloseComponent } from '../../pages/tournament-close/tournament-close.component';
import { RoundCloseComponent } from '../../pages/round-close/round-close.component';
import { CourseAssignmentComponent } from '../../pages/course-assignment/course-assignment.component';
import { PairingsGenerationComponent } from '../../pages/pairings-generation/pairings-generation.component';
import { PairingsComponent } from '../../pages/pairings/pairings.component';
import { TournamentReportsComponent } from '../../pages/reports/tournament-reports.component';
import { RoundReportsComponent } from '../../pages/reports/round-reports.component';
import { PlayoffWorkspaceComponent } from '../../pages/playoffs/playoff-workspace.component';
import { SimulatorComponent } from '../../components/simulator/simulator.component';
import { ImagesComponent } from '../../pages/system/images/images.component';
import { WebhooksComponent } from '../../pages/system/webhooks/webhooks.component';

// Import guards
import { AuthGuard } from '../../guards/auth.guard';

// Import resolvers
import { TournamentResolver } from '../../resolvers/tournament.resolver';
import { CourseResolver } from '../../resolvers/course.resolver';
import { PlayerResolver } from '../../resolvers/player.resolver';
import { TournamentCourseResolver } from '../../resolvers/tournament-course.resolver';

@Injectable({
  providedIn: 'root',
})
export class NavigationRouteService {
  private componentMap: { [key: string]: Type<any> } = {
    TournamentsComponent: TournamentsComponent,
    TournamentDetailComponent: TournamentDetailComponent,
    RoundsComponent: RoundsComponent,
    ScorecardScoresComponent: ScorecardScoresComponent,
    ScorecardStatsComponent: ScorecardStatsComponent,
    ScorecardVerifyComponent: ScorecardVerifyComponent,
    PlayerDetailComponent: PlayerDetailComponent,
    TournamentCourseDetailComponent: TournamentCourseDetailComponent,
    CourseDetailComponent: CourseDetailComponent,
    CourseListComponent: CourseListComponent,
    TournamentPlayersComponent: TournamentPlayersComponent,
    PurseComponent: PurseComponent,
    ComingSoonComponent: ComingSoonComponent,
    PlayerListComponent: PlayerListComponent,
    SystemComponent: SystemComponent,
    RealTimeEventsComponent: RealTimeEventsComponent,
    ConfigComponent: ConfigComponent,
    TournamentCloseComponent: TournamentCloseComponent,
    RoundCloseComponent: RoundCloseComponent,
    CourseAssignmentComponent: CourseAssignmentComponent,
    PairingsGenerationComponent: PairingsGenerationComponent,
    PairingsComponent: PairingsComponent,
    TournamentReportsComponent: TournamentReportsComponent,
    RoundReportsComponent: RoundReportsComponent,
    PlayoffWorkspaceComponent: PlayoffWorkspaceComponent,
    SimulatorComponent: SimulatorComponent,
    ImagesComponent: ImagesComponent,
    WebhooksComponent: WebhooksComponent,
  };

  private guardMap: { [key: string]: any } = {
    AuthGuard: AuthGuard,
  };

  private resolverMap: { [key: string]: any } = {
    TournamentResolver: TournamentResolver,
    CourseResolver: CourseResolver,
    PlayerResolver: PlayerResolver,
    TournamentCourseResolver: TournamentCourseResolver,
  };

  constructor(@Optional() private loggingService?: LoggingService) {}

  /**
   * Helper method to log warnings with fallback to console.warn when LoggingService is not available
   */
  private logWarning(message: string): void {
    if (this.loggingService) {
      this.loggingService.warn('NavigationRouteService', message);
    } else {
      console.warn(message);
    }
  }

  /**
   * Converts NavigationRoute array to Angular Routes array
   */
  convertToAngularRoutes(navigationRoutes: NavigationRoute[]): Routes {
    return navigationRoutes.map((config) => this.convertSingleRoute(config));
  }

  /**
   * Converts NavigationRoute array to NavItem array for navigation
   */
  convertToNavItems(navigationRoutes: NavigationRoute[]): NavItem[] {
    return navigationRoutes
      .filter((route) => route.name) // Only include items with names
      .map((route) => this.convertToNavItem(route));
  }

  /**
   * Converts a single NavigationRoute to Angular Route
   */
  private convertSingleRoute(config: NavigationRoute): Route {
    const route: Route = {
      path: config.path,
    };

    // Handle redirectTo
    if (config.redirectTo) {
      route.redirectTo = config.redirectTo;
    }

    // Handle pathMatch
    if (config.pathMatch) {
      route.pathMatch = config.pathMatch;
    }

    // Handle component
    if (config.component) {
      const component = this.componentMap[config.component];
      if (component) {
        route.component = component;
      } else {
        this.logWarning(
          `Component ${config.component} not found in component map`
        );
      }
    }

    // Handle canActivate guards
    if (config.canActivate && config.canActivate.length > 0) {
      route.canActivate = config.canActivate
        .map((guardName) => {
          const guard = this.guardMap[guardName];
          if (guard) {
            return guard;
          } else {
            this.logWarning(`Guard ${guardName} not found in guard map`);
            return null;
          }
        })
        .filter((guard) => guard !== null);
    }

    // Handle resolvers
    if (config.resolve) {
      route.resolve = {};
      Object.keys(config.resolve).forEach((key) => {
        const resolverName = config.resolve![key];
        const resolver = this.resolverMap[resolverName];
        if (resolver) {
          route.resolve![key] = resolver;
        } else {
          this.logWarning(`Resolver ${resolverName} not found in resolver map`);
        }
      });
    }

    // Handle data - merge pageTitle into existing data
    if (config.data || config.pageTitle) {
      route.data = {
        ...config.data,
        ...(config.pageTitle && { pageTitle: config.pageTitle }),
      };
    }

    // Handle children routes
    if (config.children && config.children.length > 0) {
      route.children = config.children.map((child) =>
        this.convertSingleRoute(child)
      );
    }

    return route;
  }

  /**
   * Converts a single NavigationRoute to NavItem
   */
  private convertToNavItem(
    config: NavigationRoute,
    parentPath: string = ''
  ): NavItem {
    // Build the full URL path
    const fullPath = parentPath ? `${parentPath}/${config.path}` : config.path;

    const navItem: NavItem = {
      name: config.name!,
      url: `/${fullPath}`,
      icon: config.icon,
      hidden: config.hidden ?? false,
      readonly: config.readonly ?? false,
    };

    // Handle children navigation items
    if (config.children && config.children.length > 0) {
      const childNavItems = config.children
        .filter((child) => child.name)
        .map((child) => this.convertToNavItem(child, fullPath));

      if (childNavItems.length > 0) {
        navItem.children = childNavItems;
      }
    }

    return navItem;
  }
}
